/**
 * Shared header-controls voor het Onderwater venster — gebruikt door
 * zowel TextPane (text-modus) als GridPane (tabel-modus). Bevat de
 * vijf knoppen die per ontwerp 2026-05-23 rechts in de header moeten
 * staan:
 *
 *   [ModeSwitcher (als preview aan)] [Preview] [Copy] [Pin] [Verberg]
 *
 * `copyText` komt uit `useRevealEmitters(lang).currentText` — caller-
 * specifiek zodat zowel TextPane (live-getypte tekst) als GridPane
 * (geëmitte tekst van de actieve mode) dezelfde knop kunnen gebruiken
 * met hun eigen tekst-bron.
 *
 * State (mode, preview-open, pinned, open) komt uit useUi zodat beide
 * panes automatisch in-sync zijn: schakel mode in TextPane, GridPane
 * volgt direct.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type PlantumlLineType, useUi } from "../../state/ui";
import type { RevealMode } from "./revealText";

export function RevealPaneHeaderControls({ copyText }: { copyText: string }) {
  const { t } = useTranslation();
  const mode = useUi((s) => s.revealMode);
  const setMode = useUi((s) => s.setRevealMode);
  const previewOpen = useUi((s) => s.revealPreviewOpen);
  const setPreviewOpen = useUi((s) => s.setRevealPreviewOpen);
  const lineType = useUi((s) => s.plantumlLineType);
  const setLineType = useUi((s) => s.setPlantumlLineType);
  const pinned = useUi((s) => s.textPanePinned);
  const setPinned = useUi((s) => s.setTextPanePinned);
  const setOpen = useUi((s) => s.setTextPaneOpen);

  return (
    <div className="flex items-center gap-2">
      {previewOpen && <ModeSwitcher mode={mode} onChange={setMode} />}
      {previewOpen && mode === "plantuml" && (
        <LineTypeDropdown lineType={lineType} onChange={setLineType} />
      )}
      <button
        type="button"
        className={`rounded border border-(--ld-control) px-2 py-1 font-medium text-sm hover:bg-(--ld-raised) ${
          previewOpen ? "bg-(--ld-active) text-(--ld-accent)" : "bg-(--ld-surface) text-(--ld-text)"
        }`}
        onClick={() => setPreviewOpen(!previewOpen)}
        title={previewOpen ? t("textPane.preview.hideTitle") : t("textPane.preview.showTitle")}
        aria-pressed={previewOpen}
      >
        {previewOpen ? t("textPane.preview.on") : t("textPane.preview.off")}
      </button>
      <CopyButton text={copyText} />
      <button
        type="button"
        className={`rounded px-1 text-base leading-none ${
          pinned
            ? "text-(--ld-accent) hover:text-(--ld-accent)"
            : "text-(--ld-muted) hover:text-(--ld-text)"
        }`}
        onClick={() => setPinned(!pinned)}
        title={pinned ? t("textPane.pin.pinnedTitle") : t("textPane.pin.unpinnedTitle")}
        aria-label={pinned ? t("diagramsPanel.pin.unpinAria") : t("diagramsPanel.pin.pinAria")}
        aria-pressed={pinned}
      >
        {pinned ? "📌" : "📍"}
      </button>
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
        onClick={() => setOpen(false)}
        title={t("textPane.hideHint")}
      >
        {t("textPane.hideButton")}
      </button>
    </div>
  );
}

/** Drie-knop segmented switcher voor PlantUML / Mermaid / SVG. */
function ModeSwitcher({ mode, onChange }: { mode: RevealMode; onChange: (m: RevealMode) => void }) {
  const opts: Array<{ id: RevealMode; label: string }> = [
    { id: "plantuml", label: "PlantUML" },
    { id: "mermaid", label: "Mermaid" },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded border border-(--ld-control) text-xs">
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`px-2 py-0.5 ${
            mode === o.id
              ? "bg-(--ld-primary) font-semibold text-(--ld-primary-ink)"
              : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
          }`}
          onClick={() => onChange(o.id)}
          aria-pressed={mode === o.id}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Dropdown voor PlantUML lijn-routing — `curved` (default) / `polyline` /
 * `orthogonaal`. Alleen zichtbaar bij PlantUML+preview. Persistent via
 * useUi → localStorage.
 */
function LineTypeDropdown({
  lineType,
  onChange,
}: {
  lineType: PlantumlLineType;
  onChange: (lt: PlantumlLineType) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="inline-flex items-center gap-1 text-(--ld-muted) text-xs">
      <span>{t("textPane.lineType.label")}</span>
      <select
        className="rounded border border-(--ld-muted) bg-(--ld-surface) px-1 py-0.5 text-(--ld-text) text-xs focus:border-(--ld-accent) focus:outline-none"
        value={lineType}
        onChange={(e) => onChange(e.target.value as PlantumlLineType)}
        title={t("textPane.lineType.title")}
      >
        <option value="default">{t("textPane.lineType.curved")}</option>
        <option value="polyline">{t("textPane.lineType.polyline")}</option>
        <option value="ortho">{t("textPane.lineType.ortho")}</option>
      </select>
    </label>
  );
}

/** Copy-to-clipboard button met korte "kopieerd!"-feedback. */
function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 font-medium text-(--ld-text) text-sm hover:bg-(--ld-raised)"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard-API kan falen in sommige browser-contexten;
          // silent fallback (de tekst is visueel selecteerbaar).
        }
      }}
      title={t("textPane.copy.title")}
    >
      {copied ? t("textPane.copy.copied") : t("textPane.copy.label")}
    </button>
  );
}
